import React, { useMemo } from 'react';

import { ScheduleEntry } from 'us-web-services';
import { useSharedTranslation } from '../../hooks/useSharedTranslation';
import { Button, ButtonType } from '../../Button/Button';
import {
  MONTHS,
  WEEKDAYS,
  daysInMonth,
  formatTime,
  fromIsoToDateKey,
  isSameDay,
  startOfMonth,
  toDateKey,
} from '../utils';

interface Props {
  cursor: Date;
  schedule: ScheduleEntry[];
  canEdit: boolean;
  onCreateOnDay: (day: Date) => void;
  onOpenBooking: (entry: ScheduleEntry) => void;
  onEditAvailability: (entry: ScheduleEntry) => void;
  onJumpToDay: (day: Date) => void;
  namespace?: string;
  showBookingDetails?: boolean;
}

export const MonthView = ({
  cursor,
  schedule,
  canEdit,
  onCreateOnDay,
  onOpenBooking,
  onEditAvailability,
  onJumpToDay,
  namespace,
  showBookingDetails,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespace);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const cells = useMemo<Array<{ key: string; date?: Date; inMonth: boolean }>>(() => {
    const first = startOfMonth(year, month);
    const firstWeekday = first.getDay();
    const total = daysInMonth(year, month);
    const out: Array<{ key: string; date?: Date; inMonth: boolean }> = [];
    for (let i = 0; i < firstWeekday; i += 1) {
      out.push({ key: `pad-${i}`, inMonth: false });
    }
    for (let d = 1; d <= total; d += 1) {
      const date = new Date(year, month, d);
      out.push({ key: toDateKey(date), date, inMonth: true });
    }
    while (out.length % 7 !== 0) {
      out.push({ key: `padend-${out.length}`, inMonth: false });
    }
    return out;
  }, [year, month]);

  const byDay = useMemo(() => {
    const map = new Map<string, ScheduleEntry[]>();
    for (const entry of schedule) {
      const key = fromIsoToDateKey(entry.start);
      if (!key) continue;
      const arr = map.get(key) ?? [];
      arr.push(entry);
      map.set(key, arr);
    }
    return map;
  }, [schedule]);

  const today = new Date();
  const renderBookingDetails = showBookingDetails ?? canEdit;

  return (
    <div className='bg-white border-2 border-sapphire-50 rounded-[20px] break-words p-[15px] mb-[15px]'>
      <div className='font-semibold mb-2'>
        {MONTHS[month]} {year}
      </div>
      <div className='grid grid-cols-7 gap-1 mb-1 text-center'>
        {WEEKDAYS.map((w) => (
          <div key={w} className='text-xs font-semibold text-neutral-600 py-1'>
            {w}
          </div>
        ))}
      </div>
      <div className='grid grid-cols-7 gap-1'>
        {cells.map((cell) => {
          if (!cell.date) {
            return <div key={cell.key} className='min-h-20' />;
          }
          const cellDate = cell.date;
          const isToday = isSameDay(cellDate, today);
          const entries = byDay.get(cell.key) ?? [];
          return (
            <div
              key={cell.key}
              className={`min-h-20 border rounded p-1 text-left ${
                isToday ? 'bg-sapphire-50 border-sapphire' : 'border-neutral-200'
              }`}
            >
              <div className='flex justify-between items-center'>
                <Button
                  buttonType={ButtonType.tertiary}
                  onClick={(): void => onJumpToDay(cellDate)}
                  className={`text-xs ${isToday ? 'font-semibold' : ''}`}
                  title={t('pages.calendar.goToDay')}
                >
                  {cellDate.getDate()}
                </Button>
                {canEdit && cellDate.getTime() >= new Date().setHours(0, 0, 0, 0) && (
                  <Button
                    buttonType={ButtonType.tertiary}
                    aria-label={t('pages.calendar.addOnDateAriaLabel', { date: cellDate.toDateString() })}
                    className='text-neutral-400 hover:text-sapphire text-xs'
                    onClick={(): void => onCreateOnDay(cellDate)}
                  >
                    +
                  </Button>
                )}
              </div>
              <div className='space-y-1 mt-0.5'>
                {entries.slice(0, 3).map((entry) => {
                  if (entry.booking) {
                    const pending =
                      entry.booking.status?.code === 'parent_booking' ||
                      entry.booking.status?.code === 'parent_reschedule' ||
                      entry.booking.status?.code === 'sitter_reschedule';
                    const parent = entry.booking.parent;
                    const firstName = parent?.firstName ?? parent?.name ?? t('pages.calendar.booking');
                    return (
                      <Button
                        key={entry.id ?? entry.start}
                        buttonType={ButtonType.tertiary}
                        onClick={(): void => onOpenBooking(entry)}
                        className={`flex items-center gap-1 w-full !rounded px-1 py-0.5 text-left !text-[10px] leading-tight ${
                          pending
                            ? 'bg-honey-100 !text-neutral-800 hover:!text-neutral-900 hover:bg-honey-200'
                            : 'bg-sapphire !text-white hover:!text-white hover:bg-sapphire-700'
                        }`}
                      >
                        {renderBookingDetails && parent?.pictureUrl && (
                          <img
                            alt=''
                            src={parent.pictureUrl}
                            className='w-4 h-4 rounded-full object-cover flex-shrink-0'
                          />
                        )}
                        <span className='truncate font-semibold'>
                          {renderBookingDetails
                            ? firstName
                            : pending
                              ? t('pages.calendar.bookingRequest')
                              : t('pages.calendar.booked')}
                        </span>
                        <span className='ml-auto opacity-90'>
                          {formatTime(entry.start)}
                        </span>
                      </Button>
                    );
                  }
                  return (
                    <Button
                      key={entry.id ?? entry.start}
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => onEditAvailability(entry)}
                      className='flex items-center gap-1 w-full !rounded px-1 py-0.5 text-left !text-[10px] leading-tight bg-emerald-100 !text-emerald-900 hover:!text-emerald-900 hover:bg-emerald-200'
                    >
                      <span className='font-semibold'>{t('pages.calendar.availableShort')}</span>
                      <span className='ml-auto opacity-80'>
                        {t('common.timeRange', { start: formatTime(entry.start), end: entry.pastMidnight ? t('pages.calendar.midnightPlus') : formatTime(entry.end) })}
                      </span>
                    </Button>
                  );
                })}
                {entries.length > 3 && (
                  <div className='text-[10px] text-neutral-500'>
                    {t('pages.calendar.moreEntries', { count: entries.length - 3 })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
